import { useQuery } from '@tanstack/react-query'
import { appointmentApi } from '@services/appointment.service'
import { Clock, CalendarDays, CalendarX } from 'lucide-react'
import type { DoctorAvailabilityRule } from '@/types'
import { formatDisplayDate } from './time-utils'

interface Props { doctorId: string }

function calcHoursFromRule(rule: DoctorAvailabilityRule): number {
  if (!rule.work_days?.length || !rule.work_start_time || !rule.work_end_time) return 0
  const [sh, sm] = rule.work_start_time.split(':').map(Number)
  const [eh, em] = rule.work_end_time.split(':').map(Number)
  return rule.work_days.length * ((eh * 60 + em - sh * 60 - sm) / 60)
}

function calcHoursFromWindows(items: Array<{ start_time: string; end_time: string }>): number {
  return items.reduce((acc, w) => {
    const [sh, sm] = w.start_time.split(':').map(Number)
    const [eh, em] = w.end_time.split(':').map(Number)
    return acc + (eh * 60 + em - sh * 60 - sm) / 60
  }, 0)
}

export function AvailabilitySummaryStrip({ doctorId }: Props) {
  const { data: rule } = useQuery({
    queryKey: ['availability-rule', doctorId],
    queryFn: () => appointmentApi.getAvailabilityRule(doctorId),
  })
  const { data: weekly } = useQuery({
    queryKey: ['weekly-availability', doctorId],
    queryFn: () => appointmentApi.getWeeklyAvailability(doctorId),
  })
  const { data: overrides } = useQuery({
    queryKey: ['availability-overrides', doctorId],
    queryFn: () => appointmentApi.getAvailabilityOverrides(doctorId),
  })

  const windows = weekly?.items ?? []
  const isGranular = rule?.work_days == null

  const weeklyHours = rule
    ? isGranular ? calcHoursFromWindows(windows) : calcHoursFromRule(rule)
    : 0
  const activeDays = rule
    ? isGranular
      ? new Set(windows.map((w) => w.weekday)).size
      : (rule.work_days?.length ?? 0)
    : 0

  const today = new Date().toISOString().slice(0, 10)
  const nextOverride = [...(overrides?.items ?? [])]
    .filter((o) => o.override_date >= today)
    .sort((a, b) => a.override_date.localeCompare(b.override_date))[0]

  const displayHours =
    weeklyHours > 0
      ? `${weeklyHours % 1 === 0 ? weeklyHours : weeklyHours.toFixed(1)}h`
      : null

  return (
    <div className="grid grid-cols-3 divide-x overflow-hidden rounded-xl border bg-card shadow-sm">
      {/* Hours / week — green accent (availability signal) */}
      <div className="flex flex-col">
        <div className="h-0.5 bg-[#2d7a5e]" />
        <div className="flex items-center gap-3 px-4 py-3.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#2d7a5e]/10">
            <Clock className="h-4 w-4 text-[#2d7a5e]" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Horas / semana</p>
            <p className="text-sm font-semibold">
              {displayHours ?? (
                <span className="font-normal text-muted-foreground">Sin configurar</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Active days — forest-green accent (primary brand) */}
      <div className="flex flex-col">
        <div className="h-0.5 bg-ml-primary" />
        <div className="flex items-center gap-3 px-4 py-3.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-ml-primary/10">
            <CalendarDays className="h-4 w-4 text-ml-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Días activos</p>
            <p className="text-sm font-semibold">
              {activeDays > 0 ? (
                `${activeDays} de 7`
              ) : (
                <span className="font-normal text-muted-foreground">—</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Next exception — amber accent (alert/calendar signal) */}
      <div className="flex flex-col">
        <div className="h-0.5 bg-amber-500" />
        <div className="flex items-center gap-3 px-4 py-3.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
            <CalendarX className={`h-4 w-4 ${nextOverride ? 'text-amber-600' : 'text-amber-400'}`} />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Próxima excepción</p>
            {nextOverride ? (
              <p className="truncate text-sm font-semibold text-amber-700">
                {formatDisplayDate(nextOverride.override_date, { day: 'numeric', month: 'short' })}
                {nextOverride.note && (
                  <span className="ml-1 text-xs font-normal text-muted-foreground">
                    ({nextOverride.note})
                  </span>
                )}
              </p>
            ) : (
              <p className="text-sm font-normal text-muted-foreground">Sin excepciones</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
